import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import type { JobRecommendation } from '../../react-app/routes/discover';
import { Badge } from '../ui';
import { X, Building, MapPin, DollarSign, Clock, ExternalLink, Loader2 } from 'lucide-react';
import { apiFetch } from '../../lib/api-client';
import { useResumeContext } from '../../hooks/useResumeContext';

// API response types
interface FitAnalysis {
  jobId: string;
  resumeId: string;
  overallScore: number;
  skillsMatch: {
    matching: string[];
    missing: string[];
    score: number;
  };
  requirements: {
    mustHave: { total: number; matched: number };
    niceToHave: { total: number; matched: number };
  };
  recommendations: string[];
}

interface StrategySnapshot {
  resumeId: string;
  objective: string;
  exploration: number;
  topStrategies: Array<{
    name: string;
    weight: number;
    successRate: number;
    avgResponseTime: string;
    interviewRate: number;
  }>;
  recentActivity: Array<{
    type: string;
    description: string;
    timestamp: string;
  }>;
}

export function JobRightSheet({
  open,
  job,
  onClose,
  onSave,
  onApply,
  onReject,
  onFeedback,
  optimisticAction,
}: {
  open: boolean;
  job?: JobRecommendation;
  onClose: () => void;
  onSave: (job: JobRecommendation) => void;
  onApply: (job: JobRecommendation) => void;
  onReject: (job: JobRecommendation) => void;
  onFeedback: (job: JobRecommendation, signal: string) => void;
  optimisticAction?: 'saving' | 'saved' | 'applying' | 'applied' | 'rejecting' | 'rejected';
}) {
  const { selectedResumeId } = useResumeContext();
  const [tab, setTab] = useState<'desc' | 'fit' | 'strategy'>('desc');
  const [fitAnalysis, setFitAnalysis] = useState<FitAnalysis | null>(null);
  const [strategySnapshot, setStrategySnapshot] = useState<StrategySnapshot | null>(null);
  const [fitLoading, setFitLoading] = useState(false);
  const [strategyLoading, setStrategyLoading] = useState(false);
  
  // Reset to description tab when sheet opens
  useEffect(() => { 
    if (open) {
      setTab('desc');
      setFitAnalysis(null);
      setStrategySnapshot(null);
    }
  }, [open]);

  // Load fit analysis when fit tab is selected
  useEffect(() => {
    if (tab === 'fit' && job && selectedResumeId && !fitAnalysis) {
      loadFitAnalysis();
    }
  }, [tab, job, selectedResumeId, fitAnalysis]);

  // Load strategy snapshot when strategy tab is selected
  useEffect(() => {
    if (tab === 'strategy' && selectedResumeId && !strategySnapshot) {
      loadStrategySnapshot();
    }
  }, [tab, selectedResumeId, strategySnapshot]);

  const loadFitAnalysis = async () => {
    if (!job || !selectedResumeId) return;
    
    try {
      setFitLoading(true);
      const response = await apiFetch(`/api/v2/jobs/${encodeURIComponent(job.jobId || job.id || '')}/fit?resumeId=${selectedResumeId}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setFitAnalysis(data.data);
        }
      }
    } catch (error) {
      console.error('Failed to load fit analysis:', error);
    } finally {
      setFitLoading(false);
    }
  };

  const loadStrategySnapshot = async () => {
    if (!selectedResumeId) return;
    
    try {
      setStrategyLoading(true);
      const response = await apiFetch(`/api/v2/strategy/snapshot?resumeId=${selectedResumeId}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStrategySnapshot(data.data);
        }
      }
    } catch (error) {
      console.error('Failed to load strategy snapshot:', error);
    } finally {
      setStrategyLoading(false);
    }
  };

  const formatStrategy = (strategy: string) => {
    switch (strategy) {
      case 'HIGH_COMP': return 'High Compensation';
      case 'RESUME_TAGS': return 'Resume Tags';
      case 'EASY_WIN': return 'Easy Wins';
      case 'SMALL_CO': return 'Small Companies';
      case 'REMOTE': return 'Remote Focus';
      default: return strategy;
    }
  };

  const getStrategyColor = (strategy: string): 'neutral' | 'info' | 'positive' | 'secondary' | 'primary' | 'caution' | 'success' => {
    switch (strategy) {
      case 'HIGH_COMP': return 'info';
      case 'RESUME_TAGS': return 'primary';
      case 'EASY_WIN': return 'positive';
      case 'SMALL_CO': return 'caution';
      case 'REMOTE': return 'neutral';
      default: return 'neutral';
    }
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200" 
          enterFrom="opacity-0" 
          enterTo="opacity-100"
          leave="ease-in duration-150" 
          leaveFrom="opacity-100" 
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        {/* Sheet container */}
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 flex justify-end">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-out duration-200"
              enterFrom="translate-x-full" 
              enterTo="translate-x-0"
              leave="transform transition ease-in duration-150"
              leaveFrom="translate-x-0" 
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="h-full w-full max-w-[520px] rounded-l-2xl border border-border bg-card shadow-card">
                <div className="flex h-full flex-col">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-3 border-b border-border p-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Building className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <Dialog.Title className="text-base font-semibold leading-6 text-foreground">
                          {job?.title || 'Job details'}
                        </Dialog.Title>
                        <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                          {job?.company && (
                            <span className="flex items-center gap-1">
                              <Building className="h-3 w-3" />
                              {job.company}
                            </span>
                          )}
                          {job?.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {job.location}
                            </span>
                          )}
                          {job?.salary && (
                            <span className="flex items-center gap-1">
                              <DollarSign className="h-3 w-3" />
                              {job.salary}
                            </span>
                          )}
                          {job?.employment_type && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {job.employment_type}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Header actions */}
                    <div className="flex items-center gap-2">
                      {job && (
                        <>
                          <button 
                            onClick={() => onSave(job)} 
                            disabled={optimisticAction === 'saving' || optimisticAction === 'saved'}
                            className={`rounded-xl border px-3 py-1.5 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all text-sm flex items-center gap-1 ${
                              optimisticAction === 'saving' || optimisticAction === 'saved'
                                ? 'border-positive bg-positive/10 text-positive cursor-not-allowed'
                                : 'border-border'
                            }`}
                          >
                            {optimisticAction === 'saving' && <Loader2 className="h-3 w-3 animate-spin" />}
                            {optimisticAction === 'saved' && <div className="h-3 w-3 rounded-full bg-positive flex items-center justify-center">
                              <div className="h-1.5 w-1.5 rounded-full bg-positive-foreground"></div>
                            </div>}
                            {optimisticAction === 'saved' ? 'Saved' : optimisticAction === 'saving' ? 'Saving...' : 'Save'}
                          </button>
                          <button 
                            onClick={() => onApply(job)} 
                            disabled={optimisticAction === 'applying' || optimisticAction === 'applied'}
                            className={`rounded-xl px-3 py-1.5 hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all text-sm flex items-center gap-1 ${
                              optimisticAction === 'applying' || optimisticAction === 'applied'
                                ? 'bg-positive text-positive-foreground cursor-not-allowed'
                                : 'bg-primary text-primary-foreground'
                            }`}
                          >
                            {optimisticAction === 'applying' && <Loader2 className="h-3 w-3 animate-spin" />}
                            {optimisticAction === 'applied' && <div className="h-3 w-3 rounded-full bg-positive-foreground flex items-center justify-center">
                              <div className="h-1.5 w-1.5 rounded-full bg-positive"></div>
                            </div>}
                            {optimisticAction === 'applied' ? 'Applied' : optimisticAction === 'applying' ? 'Applying...' : 'Apply'}
                          </button>
                        </>
                      )}
                      <button 
                        onClick={onClose} 
                        className="rounded-xl border border-border p-1.5 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Tab Navigation */}
                  <div className="px-4 pt-3">
                    <div className="mb-4 flex gap-4 border-b border-border text-sm">
                      <TabButton active={tab === 'desc'} onClick={() => setTab('desc')}>
                        Description
                      </TabButton>
                      <TabButton active={tab === 'fit'} onClick={() => setTab('fit')}>
                        Fit Analysis
                      </TabButton>
                      <TabButton active={tab === 'strategy'} onClick={() => setTab('strategy')}>
                        Strategy
                      </TabButton>
                    </div>
                  </div>

                  {/* Tab Content */}
                  <div className="flex-1 overflow-y-auto px-4 pb-6">
                    {tab === 'desc' && (
                      <div className="space-y-4">
                        {/* Company info */}
                        <div className="rounded-2xl border border-border bg-muted/20 p-4">
                          <h4 className="font-medium text-foreground mb-2">About {job?.company}</h4>
                          <div className="text-sm text-muted-foreground space-y-1">
                            <div>Technology company • 100-500 employees</div>
                            <div>Founded 2018 • Series B</div>
                            <div className="flex items-center gap-1 mt-2">
                              <ExternalLink className="h-3 w-3" />
                              <span>View company profile</span>
                            </div>
                          </div>
                        </div>

                        {/* Job description */}
                        <div>
                          <h4 className="font-medium text-foreground mb-3">Job Description</h4>
                          <div className="prose prose-sm max-w-none text-muted-foreground">
                            <p>
                              {job?.description || 
                                'We are looking for a talented software engineer to join our platform team. You will work on building scalable infrastructure and developer tools that power our entire product suite.'
                              }
                            </p>
                            <p className="mt-4">
                              This is a senior-level position requiring deep technical expertise and the ability to work independently on complex problems. You'll collaborate with product, design, and other engineering teams to deliver high-quality software.
                            </p>
                          </div>
                        </div>

                        {/* Requirements */}
                        <div>
                          <h4 className="font-medium text-foreground mb-3">Requirements</h4>
                          <ul className="space-y-2 text-sm text-muted-foreground">
                            <li className="flex items-start gap-2">
                              <span className="h-1.5 w-1.5 bg-muted-foreground rounded-full mt-2 shrink-0" />
                              5+ years of software engineering experience
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="h-1.5 w-1.5 bg-muted-foreground rounded-full mt-2 shrink-0" />
                              Strong experience with TypeScript and React
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="h-1.5 w-1.5 bg-muted-foreground rounded-full mt-2 shrink-0" />
                              Experience with cloud platforms (AWS, GCP, Azure)
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="h-1.5 w-1.5 bg-muted-foreground rounded-full mt-2 shrink-0" />
                              Platform engineering or DevOps experience
                            </li>
                          </ul>
                        </div>

                        {/* Benefits */}
                        <div>
                          <h4 className="font-medium text-foreground mb-3">Benefits</h4>
                          <ul className="space-y-2 text-sm text-muted-foreground">
                            <li className="flex items-start gap-2">
                              <span className="h-1.5 w-1.5 bg-positive rounded-full mt-2 shrink-0" />
                              Competitive salary and equity package
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="h-1.5 w-1.5 bg-positive rounded-full mt-2 shrink-0" />
                              Flexible remote work arrangements
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="h-1.5 w-1.5 bg-positive rounded-full mt-2 shrink-0" />
                              Health, dental, and vision insurance
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="h-1.5 w-1.5 bg-positive rounded-full mt-2 shrink-0" />
                              Learning and development budget
                            </li>
                          </ul>
                        </div>
                      </div>
                    )}
                    
                    {tab === 'fit' && (
                      <div className="space-y-4">
                        {fitLoading ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            <span className="ml-2 text-sm text-muted-foreground">Analyzing fit...</span>
                          </div>
                        ) : fitAnalysis ? (
                          <>
                            {/* Resume overlap */}
                            <div className="rounded-2xl border border-border bg-muted/20 p-4">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium">Overall Fit Score</span>
                                <span className="rounded-full bg-positive/20 px-2 py-0.5 text-xs text-positive-foreground font-medium">
                                  {Math.round(fitAnalysis.overallScore * 100)}%
                                </span>
                              </div>
                              <div className="w-full bg-muted rounded-full h-2">
                                <div 
                                  className="bg-positive h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${fitAnalysis.overallScore * 100}%` }}
                                />
                              </div>
                            </div>

                            {/* Requirements match */}
                            <div>
                              <h4 className="font-medium text-foreground mb-3">Requirements Analysis</h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex items-center justify-between">
                                  <span>Must-haves</span>
                                  <span className="font-medium">
                                    {fitAnalysis.requirements.mustHave.matched}/{fitAnalysis.requirements.mustHave.total}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span>Nice-to-haves</span>
                                  <span className="font-medium">
                                    {fitAnalysis.requirements.niceToHave.matched}/{fitAnalysis.requirements.niceToHave.total}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Skills match */}
                            <div>
                              <h4 className="font-medium text-foreground mb-3">Skills Match</h4>
                              <div className="flex flex-wrap gap-2 text-xs">
                                {fitAnalysis.skillsMatch.matching.map((skill) => (
                                  <span key={skill} className="rounded-full border border-positive px-2.5 py-0.5 text-positive bg-positive/10">
                                    ✓ {skill}
                                  </span>
                                ))}
                                {fitAnalysis.skillsMatch.missing.slice(0, 3).map((skill) => (
                                  <span key={skill} className="rounded-full border border-muted px-2.5 py-0.5 text-muted-foreground">
                                    {skill} ✕
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* Recommendations */}
                            <div>
                              <h4 className="font-medium text-foreground mb-3">AI Recommendations</h4>
                              <div className="space-y-2 text-sm text-muted-foreground">
                                {fitAnalysis.recommendations.map((rec, index) => (
                                  <div key={index}>• {rec}</div>
                                ))}
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="text-center py-8 text-muted-foreground">
                            <p>Failed to load fit analysis</p>
                            <button 
                              onClick={loadFitAnalysis}
                              className="mt-2 text-sm text-primary hover:underline"
                            >
                              Try again
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {tab === 'strategy' && (
                      <div className="space-y-4">
                        {strategyLoading ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            <span className="ml-2 text-sm text-muted-foreground">Loading strategy data...</span>
                          </div>
                        ) : (
                          <>
                            {/* Strategy info */}
                            <div className="rounded-2xl border border-border bg-muted/20 p-4">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-muted-foreground">Suggested by</span>
                                {job && (
                                  <Badge variant={getStrategyColor(job.strategy)} className="text-xs">
                                    {formatStrategy(job.strategy)}
                                  </Badge>
                                )}
                              </div>
                              {job?.confidence && (
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm text-muted-foreground">Confidence</span>
                                  <span className="font-medium">{Math.round(job.confidence * 100)}%</span>
                                </div>
                              )}
                              {strategySnapshot && (
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-muted-foreground">Current Objective</span>
                                  <span className="font-medium">{strategySnapshot.objective}</span>
                                </div>
                              )}
                            </div>

                            {/* Why this job */}
                            <div>
                              <h4 className="font-medium text-foreground mb-3">Why this job</h4>
                              <p className="text-sm text-muted-foreground">
                                {job?.rationale || 'This job matches your profile based on resume analysis and strategy preferences.'}
                              </p>
                            </div>

                            {/* Strategy performance */}
                            {strategySnapshot && (
                              <div>
                                <h4 className="font-medium text-foreground mb-3">Strategy Performance</h4>
                                {strategySnapshot.topStrategies.map((strategy) => (
                                  <div key={strategy.name} className="mb-3 p-3 rounded-lg border border-border">
                                    <div className="font-medium text-sm mb-2">{formatStrategy(strategy.name)}</div>
                                    <div className="space-y-1 text-xs text-muted-foreground">
                                      <div className="flex justify-between">
                                        <span>Success Rate</span>
                                        <span>{Math.round(strategy.successRate * 100)}%</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span>Avg Response Time</span>
                                        <span>{strategy.avgResponseTime}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span>Interview Rate</span>
                                        <span>{Math.round(strategy.interviewRate * 100)}%</span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Recent Activity */}
                            {strategySnapshot && strategySnapshot.recentActivity.length > 0 && (
                              <div>
                                <h4 className="font-medium text-foreground mb-3">Recent Activity</h4>
                                <div className="space-y-2 text-sm">
                                  {strategySnapshot.recentActivity.slice(0, 3).map((activity, index) => (
                                    <div key={index} className="flex items-start gap-2">
                                      <span className="text-muted-foreground text-xs mt-1">
                                        {new Date(activity.timestamp).toLocaleDateString()}
                                      </span>
                                      <span className="text-muted-foreground flex-1">{activity.description}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Teach the model */}
                            <div>
                              <h4 className="font-medium text-foreground mb-3">Teach the model</h4>
                              <p className="text-sm text-muted-foreground mb-3">
                                Help improve future recommendations by providing feedback.
                              </p>
                              <div className="flex flex-wrap gap-2 text-xs">
                                {job && ['too_senior', 'wrong_stack', 'low_pay', 'too_far', 'not_industry'].map((signal) => (
                                  <button 
                                    key={signal} 
                                    onClick={() => onFeedback(job, signal)} 
                                    className="rounded-xl border border-border px-2.5 py-1 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
                                  >
                                    {formatFeedbackLabel(signal)}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="pt-4 border-t border-border">
                              <button 
                                className="rounded-xl border border-border px-3 py-1.5 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all text-sm w-full" 
                                onClick={() => job && onReject(job)}
                              >
                                Reject & Hide Similar Jobs
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

function TabButton({ 
  active, 
  onClick, 
  children 
}: { 
  active: boolean; 
  onClick: () => void; 
  children: React.ReactNode 
}) {
  return (
    <button
      className={`-mb-px border-b-2 px-1.5 pb-2 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40 ${
        active 
          ? 'border-primary text-foreground' 
          : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function formatFeedbackLabel(key: string): string {
  switch (key) {
    case 'too_senior': return 'Too senior';
    case 'wrong_stack': return 'Wrong stack';
    case 'low_pay': return 'Low pay';
    case 'too_far': return 'Too far';
    case 'not_industry': return 'Not my industry';
    default: return key;
  }
}